import { create } from "zustand";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../utils/createApi";
import { createMonitoringAPICall } from "../../../utils/createMonitoringAPICall";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { AxiosError, AxiosResponse } from "axios";
import {
  FiltersPatrols,
  PatrolGroup,
  PatrolHistoryAPIResponse,
  PatrolTableData,
  PatrolAlert,
  PatrolCHMIssue,
} from "../patrolsUtils/patrolsInterfaces";
import {
  formatPatrolDate,
  formatPatrolTime,
} from "../patrolsUtils/formatPatrolTimestamp";

interface PatrolsState {
  // Basic data state
  patrolsData: any[];
  setPatrolsData: (data: any[] | ((prevState: any[]) => any[])) => void;

  // Loading states
  patrolsPageBackdropLoading: boolean;
  setPatrolsPageBackdropLoading: (loading: boolean) => void;
  parentAccountsLoadingPatrols: boolean;
  setParentAccountsLoadingPatrols: (
    loading: boolean | ((prevState: boolean) => boolean)
  ) => void;
  loadingSitesPatrols: boolean;
  setLoadingSitesPatrols: (
    loading: boolean | ((prevState: boolean) => boolean)
  ) => void;
  loadingCamerasPatrols: boolean;
  setLoadingCamerasPatrols: (loading: boolean) => void;
  patrolsHistoryLoading: boolean;
  setPatrolsHistoryLoading: (loading: boolean) => void;
  filtersAppliedPatrols: boolean;
  setFiltersAppliedPatrols: (applied: boolean) => void;

  // Pagination
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;

  // UI states
  selectedPatrol: any | null;
  setSelectedPatrol: (patrol: any | null) => void;
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  parentAccountsPatrols: PatrolGroup[];
  setParentAccountsPatrols: (
    parentAccounts:
      | PatrolGroup[]
      | ((prevState: PatrolGroup[]) => PatrolGroup[])
  ) => void;
  siteOptionsPatrols: any[];
  setSiteOptionsPatrols: (
    siteOptions: any[] | ((prevState: any[]) => any[])
  ) => void;
  cameraOptionsPatrols: any[];
  setCameraOptionsPatrols: (cameraOptions: any[]) => void;
  patrolDialogOpen: boolean;
  setPatrolDialogOpen: (patrolDialogOpen: boolean) => void;
  selectedPatrolIndex: number;
  setSelectedPatrolIndex: (selectedPatrolIndex: number) => void;

  // Search and filters
  searchQueryPatrols: string;
  setSearchQueryPatrols: (query: string) => void;
  filtersPatrols: FiltersPatrols;
  setFiltersPatrols: (
    filters: FiltersPatrols | ((prev: FiltersPatrols) => FiltersPatrols)
  ) => void;

  // Patrol dialog data
  patrolAlerts: PatrolAlert[];
  setPatrolAlerts: (alerts: PatrolAlert[]) => void;
  patrolCHMIssues: PatrolCHMIssue[];
  setPatrolCHMIssues: (issues: PatrolCHMIssue[]) => void;
  patrolSummary: string;
  setPatrolSummary: (summary: string) => void;
  patrolDialogDataLoading: boolean;
  setPatrolDialogDataLoading: (loading: boolean) => void;

  // Selected alert for video playback
  selectedPatrolAlertIndex: number;
  setSelectedPatrolAlertIndex: (index: number) => void;
  patrolAlertVideoUrl: string;
  setPatrolAlertVideoUrl: (url: string) => void;
  patrolAlertVideoLoading: boolean;
  setPatrolAlertVideoLoading: (loading: boolean) => void;

  // Selected CHM issue for image display
  selectedPatrolCHMIndex: number;
  setSelectedPatrolCHMIndex: (index: number) => void;
  patrolCHMImageUrl: string;
  setPatrolCHMImageUrl: (url: string) => void;
  patrolCHMImageLoading: boolean;
  setPatrolCHMImageLoading: (loading: boolean) => void;

  // API Actions
  fetchPatrols: (navigate: NavigateFunction) => Promise<void>;
  getPatrolGroups: (navigate: NavigateFunction) => void;
  getPatrolsHistory: (navigate: NavigateFunction) => void;
  fetchPatrolDialogData: (patrolId: string, navigate: NavigateFunction) => void;
  fetchPatrolAlertVideo: (
    windowId: string,
    navigate: NavigateFunction
  ) => void;
  fetchPatrolCHMImage: (imageS3Key: string, navigate: NavigateFunction) => void;

  // Utility functions
  resetStatePatrols: () => void;
}

export const usePatrolsStore = create<PatrolsState>((set, get) => ({
  // Basic data state
  patrolsData: [],
  setPatrolsData: (data) => {
    const newState =
      typeof data === "function" ? data(get().patrolsData) : data;
    set({ patrolsData: newState });
  },

  // Loading states
  patrolsPageBackdropLoading: false,
  setPatrolsPageBackdropLoading: (loading) =>
    set({ patrolsPageBackdropLoading: loading }),
  parentAccountsLoadingPatrols: true,
  setParentAccountsLoadingPatrols: (loading) => {
    const newState =
      typeof loading === "function"
        ? loading(get().parentAccountsLoadingPatrols)
        : loading;
    set({ parentAccountsLoadingPatrols: newState });
  },
  loadingSitesPatrols: false,
  setLoadingSitesPatrols: (loading) => {
    const newState =
      typeof loading === "function"
        ? loading(get().loadingSitesPatrols)
        : loading;
    set({ loadingSitesPatrols: newState });
  },
  loadingCamerasPatrols: false,
  setLoadingCamerasPatrols: (loading) =>
    set({ loadingCamerasPatrols: loading }),
  patrolsHistoryLoading: false,
  setPatrolsHistoryLoading: (loading) =>
    set({ patrolsHistoryLoading: loading }),
  filtersAppliedPatrols: false,
  setFiltersAppliedPatrols: (applied) =>
    set({ filtersAppliedPatrols: applied }),

  // Pagination
  page: 0,
  setPage: (page) => set({ page }),
  rowsPerPage: 10,
  setRowsPerPage: (rowsPerPage) => set({ rowsPerPage }),

  // UI states
  selectedPatrol: null,
  setSelectedPatrol: (patrol) => set({ selectedPatrol: patrol }),
  isDialogOpen: false,
  setIsDialogOpen: (open) => set({ isDialogOpen: open }),
  parentAccountsPatrols: [],
  setParentAccountsPatrols: (parentAccountsPatrols) => {
    const newState =
      typeof parentAccountsPatrols === "function"
        ? parentAccountsPatrols(get().parentAccountsPatrols)
        : parentAccountsPatrols;
    set({ parentAccountsPatrols: newState });
  },
  siteOptionsPatrols: [],
  setSiteOptionsPatrols: (siteOptionsPatrols) => {
    const newState =
      typeof siteOptionsPatrols === "function"
        ? siteOptionsPatrols(get().siteOptionsPatrols)
        : siteOptionsPatrols;
    set({ siteOptionsPatrols: newState });
  },
  cameraOptionsPatrols: [],
  setCameraOptionsPatrols: (cameraOptionsPatrols) =>
    set({ cameraOptionsPatrols }),
  selectedPatrolIndex: 0,
  setSelectedPatrolIndex: (selectedPatrolIndex) => set({ selectedPatrolIndex }),

  // Search and filters
  searchQueryPatrols: "",
  setSearchQueryPatrols: (query) => set({ searchQueryPatrols: query }),
  filtersPatrols: {
    parent_group: "",
    site: [],
    camera: [],
    start_date: null as unknown as Date,
    end_date: null as unknown as Date,
  },
  setFiltersPatrols: (filters) =>
    set((state) => ({
      filtersPatrols:
        typeof filters === "function" ? filters(state.filtersPatrols) : filters,
    })),
  patrolDialogOpen: false,
  setPatrolDialogOpen: (patrolDialogOpen) => set({ patrolDialogOpen }),

  // Patrol dialog data
  patrolAlerts: [],
  setPatrolAlerts: (alerts) => set({ patrolAlerts: alerts }),
  patrolCHMIssues: [],
  setPatrolCHMIssues: (issues) => set({ patrolCHMIssues: issues }),
  patrolSummary: "",
  setPatrolSummary: (summary) => set({ patrolSummary: summary }),
  patrolDialogDataLoading: false,
  setPatrolDialogDataLoading: (loading) =>
    set({ patrolDialogDataLoading: loading }),

  // Selected alert for video playback
  selectedPatrolAlertIndex: 0,
  setSelectedPatrolAlertIndex: (index) =>
    set({ selectedPatrolAlertIndex: index }),
  patrolAlertVideoUrl: "",
  setPatrolAlertVideoUrl: (url) => set({ patrolAlertVideoUrl: url }),
  patrolAlertVideoLoading: false,
  setPatrolAlertVideoLoading: (loading) =>
    set({ patrolAlertVideoLoading: loading }),

  // Selected CHM issue for image display
  selectedPatrolCHMIndex: 0,
  setSelectedPatrolCHMIndex: (index) => set({ selectedPatrolCHMIndex: index }),
  patrolCHMImageUrl: "",
  setPatrolCHMImageUrl: (url) => set({ patrolCHMImageUrl: url }),
  patrolCHMImageLoading: false,
  setPatrolCHMImageLoading: (loading) =>
    set({ patrolCHMImageLoading: loading }),

  // API Actions
  fetchPatrols: async (navigate) => {
    const { setPatrolsData } = get();

    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().fetchPatrols(navigate);
    };
  },
  getPatrolGroups: (navigate) => {
    const { setParentAccountsLoadingPatrols, setParentAccountsPatrols } = get();
    setParentAccountsLoadingPatrols(true);

    const AxiosUI = createApi("");

    AxiosUI.get("group/?group_type=patrol&page_size=10000").then(
      (response: AxiosResponse) => {
        setParentAccountsPatrols(response.data.results);
        setParentAccountsLoadingPatrols(false);
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(
          reason,
          navigate,
          () => get().getPatrolGroups(navigate),
          () => {
            setParentAccountsPatrols([]);
            setParentAccountsLoadingPatrols(false);
          }
        );
      }
    );
  },
  getPatrolsHistory: (navigate) => {
    const {
      filtersPatrols,
      setPatrolsData,
      setPatrolsHistoryLoading,
      setPage,
      setFiltersAppliedPatrols,
    } = get();
    setPatrolsHistoryLoading(true);

    const AxiosUI = createApi("");

    // Determine which endpoint to use based on filters
    let endpoint = "";
    if (filtersPatrols.site && filtersPatrols.site.length > 0) {
      // If site is selected, use history_by_site_id
      endpoint = `autopatrol_data/history_by_site_id/?site_id=${filtersPatrols.site[0]}`;
    } else if (filtersPatrols.parent_group) {
      // If only group is selected, use history_by_group_id
      endpoint = `autopatrol_data/history_by_group_id/?group_id=${filtersPatrols.parent_group}`;
    } else {
      // No filters selected
      setPatrolsHistoryLoading(false);
      return;
    }

    // Add optional timestamp filters (converted to Unix epoch)
    if (filtersPatrols.start_date) {
      const startDate = new Date(filtersPatrols.start_date);
      startDate.setHours(0, 0, 0, 0);
      const startTimestamp = Math.floor(startDate.getTime() / 1000);
      endpoint += `&start_timestamp=${startTimestamp}`;
    }
    if (filtersPatrols.end_date) {
      const endDate = new Date(filtersPatrols.end_date);
      endDate.setHours(23, 59, 59, 999);
      const endTimestamp = Math.floor(endDate.getTime() / 1000);
      endpoint += `&end_timestamp=${endTimestamp}`;
    }

    AxiosUI.get(endpoint).then(
      (response: AxiosResponse) => {
        const apiData: PatrolHistoryAPIResponse[] = response.data.results;

        // Transform API response to table data format
        const transformedData: PatrolTableData[] = apiData
          .map((patrol) => {
            const timestampUnix = Math.floor(
              Number.parseFloat(patrol.timestamp)
            );
            return {
              patrol_id: patrol.patrol_id,
              patrol_date: formatPatrolDate(patrol.timestamp),
              start_time: formatPatrolTime(patrol.timestamp),
              timestamp_unix: Number.isFinite(timestampUnix)
                ? timestampUnix
                : 0,
              site: patrol.site_name || "",
              schedule_name: patrol.schedule_name?.trim() || "",
              total_alerts: Array.isArray(patrol.alert_window_ids)
                ? patrol.alert_window_ids.length
                : 0,
              total_cameras: 0, // 0 for now (backend will add later)
              cameras_with_problems: 0, // 0 for now (backend will add later)
              alert_window_ids: patrol.alert_window_ids,
              site_id: patrol.site_id,
              group_id: patrol.group_id,
              schedule_id: patrol.schedule_id,
            };
          })
          .sort((a, b) => b.timestamp_unix - a.timestamp_unix);

        setPatrolsData(transformedData);
        setPage(0); // Reset to first page
        setPatrolsHistoryLoading(false);
        setFiltersAppliedPatrols(true);
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(
          reason,
          navigate,
          () => get().getPatrolsHistory(navigate),
          () => {
            setPatrolsData([]);
            setPatrolsHistoryLoading(false);
          }
        );
      }
    );
  },
  fetchPatrolDialogData: (patrolId, navigate) => {
    const {
      setPatrolAlerts,
      setPatrolCHMIssues,
      setPatrolSummary,
      setPatrolDialogDataLoading,
      setPatrolAlertVideoUrl,
      setPatrolAlertVideoLoading,
    } = get();

    // Clear previous data and set loading (reset video so next patrol doesn't show previous patrol's clip)
    setPatrolAlerts([]);
    setPatrolCHMIssues([]);
    setPatrolSummary("");
    setPatrolAlertVideoUrl("");
    setPatrolAlertVideoLoading(false);
    setPatrolDialogDataLoading(true);

    const AxiosUI = createApi("");

    // Fetch alerts, CHM issues, and patrol summary in parallel
    const alertsPromise = AxiosUI.get(
      `autopatrol_data/alerts_by_patrol_id/?patrol_id=${patrolId}`
    );
    const chmPromise = AxiosUI.get(
      `autopatrol_data/chm_issues_by_patrol_id/?patrol_id=${patrolId}`
    );
    const summaryPromise = AxiosUI.get(
      `autopatrol_data/summary_by_patrol_id/?patrol_id=${patrolId}`
    );

    Promise.all([alertsPromise, chmPromise, summaryPromise])
      .then(([alertsResponse, chmResponse, summaryResponse]) => {
        setPatrolAlerts(alertsResponse.data.alerts || []);
        setPatrolCHMIssues(chmResponse.data.chm_issues || []);
        const summaryText = summaryResponse.data?.summary?.patrol_summary ?? "";
        setPatrolSummary(summaryText);
        setPatrolDialogDataLoading(false);
      })
      .catch((reason: AxiosError) => {
        universalAPIErrorHandler(
          reason,
          navigate,
          () => get().fetchPatrolDialogData(patrolId, navigate),
          () => {
            setPatrolAlerts([]);
            setPatrolCHMIssues([]);
            setPatrolSummary("");
            setPatrolDialogDataLoading(false);
          }
        );
      });
  },
  fetchPatrolAlertVideo: (windowId, navigate) => {
    const { setPatrolAlertVideoUrl, setPatrolAlertVideoLoading } = get();

    setPatrolAlertVideoUrl("");
    setPatrolAlertVideoLoading(true);

    const AxiosUI = createMonitoringAPICall("");

    const pollForVideo = (pollUrl: string, retriesRemaining: number) => {
      setTimeout(() => {
        AxiosUI.get(pollUrl)
          .then((response: AxiosResponse) => {
            // If the response is a URL string, we're done
            if (typeof response.data === "string") {
              setPatrolAlertVideoUrl(response.data);
              setPatrolAlertVideoLoading(false);
              return;
            }

            // If still processing, keep polling
            if (
              response.data &&
              typeof response.data === "object" &&
              response.data.status === "processing"
            ) {
              if (retriesRemaining > 1) {
                pollForVideo(pollUrl, retriesRemaining - 1);
                return;
              }
            }

            // Exhausted retries or unexpected response
            setPatrolAlertVideoUrl("");
            setPatrolAlertVideoLoading(false);
          })
          .catch((error: AxiosError) => {
            const status = error.response?.status;
            // 404 means still processing, keep polling
            if (status === 404 && retriesRemaining > 1) {
              pollForVideo(pollUrl, retriesRemaining - 1);
              return;
            }

            universalAPIErrorHandler(
              error,
              navigate,
              () => get().fetchPatrolAlertVideo(windowId, navigate),
              () => {
                setPatrolAlertVideoUrl("");
                setPatrolAlertVideoLoading(false);
              }
            );
          });
      }, 500);
    };

    AxiosUI.get(`alert/clip/?window_id=${windowId}&async_generation=true`)
      .then((response: AxiosResponse) => {
        const responseData = response.data;

        // If we get a processing response, start polling
        if (
          responseData &&
          typeof responseData === "object" &&
          responseData.status === "processing"
        ) {
          const pollUrl = responseData.poll_url
            ? responseData.poll_url.replace(/^\/?monitoring-api\//, "")
            : `alert/video/?window_id=${windowId}&poll=True`;
          pollForVideo(pollUrl, 10);
          return;
        }

        // If response is a URL string, we're done
        if (typeof responseData === "string") {
          setPatrolAlertVideoUrl(responseData);
          setPatrolAlertVideoLoading(false);
          return;
        }

        // Unexpected response format
        setPatrolAlertVideoUrl("");
        setPatrolAlertVideoLoading(false);
      })
      .catch((reason: AxiosError) => {
        universalAPIErrorHandler(
          reason,
          navigate,
          () => get().fetchPatrolAlertVideo(windowId, navigate),
          () => {
            setPatrolAlertVideoUrl("");
            setPatrolAlertVideoLoading(false);
          }
        );
      });
  },
  fetchPatrolCHMImage: (imageS3Key, navigate) => {
    const { setPatrolCHMImageUrl, setPatrolCHMImageLoading } = get();

    setPatrolCHMImageUrl("");
    setPatrolCHMImageLoading(true);

    if (!imageS3Key) {
      setPatrolCHMImageLoading(false);
      return;
    }

    const AxiosUI = createApi("");

    // Replace # with %23 in the key (same as Health Monitoring does)
    const encodedKey = imageS3Key.replace(/#/g, "%23");

    AxiosUI.get(`/images/get_image_url/?frame_key=${encodedKey}`)
      .then((response: AxiosResponse) => {
        setPatrolCHMImageUrl(response.data.frame_url);
        setPatrolCHMImageLoading(false);
      })
      .catch((reason: AxiosError) => {
        universalAPIErrorHandler(
          reason,
          navigate,
          () => get().fetchPatrolCHMImage(imageS3Key, navigate),
          () => {
            setPatrolCHMImageUrl("");
            setPatrolCHMImageLoading(false);
          }
        );
      });
  },
  // Utility functions
  resetStatePatrols: () =>
    set({
      patrolsData: [],
      patrolsPageBackdropLoading: false,
      parentAccountsLoadingPatrols: true,
      loadingSitesPatrols: false,
      loadingCamerasPatrols: false,
      patrolsHistoryLoading: false,
      filtersAppliedPatrols: false,
      page: 0,
      rowsPerPage: 10,
      selectedPatrol: null,
      isDialogOpen: false,
      parentAccountsPatrols: [],
      siteOptionsPatrols: [],
      cameraOptionsPatrols: [],
      patrolDialogOpen: false,
      selectedPatrolIndex: 0,
      searchQueryPatrols: "",
      filtersPatrols: {
        parent_group: "",
        site: [],
        camera: [],
        start_date: null as unknown as Date,
        end_date: null as unknown as Date,
      },
      patrolAlerts: [],
      patrolCHMIssues: [],
      patrolSummary: "",
      patrolDialogDataLoading: false,
      selectedPatrolAlertIndex: 0,
      patrolAlertVideoUrl: "",
      patrolAlertVideoLoading: false,
      selectedPatrolCHMIndex: 0,
      patrolCHMImageUrl: "",
      patrolCHMImageLoading: false,
    }),
}));
